import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

interface ElementSize {
  width: number;
  height: number;
}

/** Current inner size of an element, `0 × 0` until it is measured. */
export const useElementSize = (ref: RefObject<HTMLElement>): ElementSize => {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const update = () => {
      const { clientWidth: width, clientHeight: height } = element;
      // Same object when nothing changed, so consumers do not re-render.
      setSize(prev =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    };
    update();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
};
